import React, { useState, useEffect } from "react";
import cloudApi from "../../../../services/cloudApi";

// ── Status config (mirrors RunDetailsModal) ────────────────────────
const STATUS_CONFIG = {
  completed: {
    label: "Completed",
    bg: "bg-emerald-500/10",
    text: "text-emerald-500",
    ring: "ring-emerald-500/20",
    icon: (
      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
      </svg>
    ),
  },
  failed: {
    label: "Failed",
    bg: "bg-red-500/10",
    text: "text-red-500",
    ring: "ring-red-500/20",
    icon: (
      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
      </svg>
    ),
  },
  running: {
    label: "Running",
    bg: "bg-blue-500/10",
    text: "text-blue-400",
    ring: "ring-blue-500/20",
    icon: (
      <svg className="w-3.5 h-3.5 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
          d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
      </svg>
    ),
  },
  pending: {
    label: "Queued",
    bg: "bg-amber-500/10",
    text: "text-amber-400",
    ring: "ring-amber-500/20",
    icon: (
      <svg className="w-3.5 h-3.5 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
          d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
      </svg>
    ),
  },
  cancelled: {
    label: "Cancelled",
    bg: "bg-gray-500/10",
    text: "text-gray-400",
    ring: "ring-gray-500/20",
    icon: (
      <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
        <rect x="6" y="6" width="12" height="12" rx="2" />
      </svg>
    ),
  },
  stopped: {
    label: "Stopped",
    bg: "bg-gray-500/10",
    text: "text-gray-400",
    ring: "ring-gray-500/20",
    icon: (
      <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
        <rect x="6" y="6" width="12" height="12" rx="2" />
      </svg>
    ),
  },
  out_of_credits: {
    label: "Out of Credits",
    bg: "bg-cyan-500/10",
    text: "text-cyan-500",
    ring: "ring-cyan-500/20",
    icon: (
      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  },
};

const DEFAULT_STATUS = {
  label: "Unknown",
  bg: "bg-gray-500/10",
  text: "text-gray-400",
  ring: "ring-gray-500/20",
  icon: (
    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
};

const RunHistoryModal = ({ automation, isOpen, onClose, onViewRunDetails }) => {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [closing, setClosing] = useState(false);

  useEffect(() => {
    if (isOpen && automation) {
      setClosing(false);
      loadRunHistory();
    }
  }, [isOpen, automation]);

  const loadRunHistory = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await cloudApi.getTaskRunHistory(automation.id, { limit: 500 });
      setRuns(response.runs || []);
    } catch (err) {
      console.error("Failed to load run history:", err);
      setError(err.message || "Failed to load run history");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setClosing(true);
    setTimeout(onClose, 120);
  };

  if (!isOpen) return null;

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = cloudApi.toLocalDate(dateString);
    if (!date) return "N/A";
    return date.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatDuration = (run) => {
    if (!run.started_at) return null;
    if (run.status === "running" || run.status === "pending") return "In progress";

    let durationSeconds = run.duration_seconds;
    if ((!durationSeconds || durationSeconds === 0) && run.completed_at) {
      const endTime = new Date(run.completed_at);
      const startTime = new Date(run.started_at);
      durationSeconds = Math.round((endTime - startTime) / 1000);
    }

    if (!durationSeconds || durationSeconds === 0) return null;
    if (durationSeconds < 60) return `${durationSeconds}s`;
    const minutes = Math.floor(durationSeconds / 60);
    const seconds = durationSeconds % 60;
    if (minutes >= 60) {
      const hours = Math.floor(minutes / 60);
      return `${hours}h ${minutes % 60}m`;
    }
    return `${minutes}m ${seconds}s`;
  };

  const getTriggerTypeLabel = (triggerType) => {
    switch (triggerType) {
      case "manual": return "Manual";
      case "scheduled": return "Scheduled";
      case "immediate": return "Immediate";
      case "recurring": return "Recurring";
      default: return triggerType || "Unknown";
    }
  };

  // Group runs by date
  const groupRunsByDate = (runs) => {
    const groups = {};
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    for (const run of runs) {
      const date = cloudApi.toLocalDate(run.started_at || run.created_at);
      if (!date) continue;

      let label;
      if (date.toDateString() === today.toDateString()) {
        label = "Today";
      } else if (date.toDateString() === yesterday.toDateString()) {
        label = "Yesterday";
      } else {
        label = date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
      }

      if (!groups[label]) groups[label] = [];
      groups[label].push(run);
    }
    return groups;
  };

  const groupedRuns = !loading && !error && runs.length > 0 ? groupRunsByDate(runs) : {};

  return (
    <div
      className={`fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 transition-opacity duration-120 ${closing ? "opacity-0" : "opacity-100"}`}
      onClick={handleClose}
    >
      <div
        className={`bg-white dark:bg-[#0c0c0f] rounded-2xl border border-gray-200 dark:border-white/[0.08] shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col overflow-hidden ${closing ? "transition-all duration-120 scale-[0.98] opacity-0" : "animate-modal-in"}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── Gradient accent line ─────────────────────────────────── */}
        <div className="h-[2px] w-full bg-gradient-to-r from-amber-400 via-orange-400 to-rose-400 opacity-60" />

        {/* ── Header ───────────────────────────────────────────────── */}
        <div className="px-5 py-3 flex items-center justify-between flex-shrink-0 border-b border-gray-100 dark:border-white/[0.06]">
          <div className="flex items-center gap-2.5 min-w-0">
            <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Run History</h2>
            <span className="text-xs text-gray-400 dark:text-gray-600 truncate">{automation?.name}</span>
            {!loading && !error && runs.length > 0 && (
              <span className="text-[11px] text-gray-400 dark:text-gray-600 tabular-nums">{runs.length} runs</span>
            )}
          </div>
          <div className="flex items-center gap-0.5">
            {!loading && !error && runs.length > 0 && (
              <>
                <button
                  onClick={loadRunHistory}
                  className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-white/[0.06] transition-all text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-200"
                  title="Refresh"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </button>
                <div className="w-px h-4 bg-gray-200 dark:bg-white/[0.08] mx-1.5" />
              </>
            )}
            <button
              onClick={handleClose}
              className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-white/[0.06] transition-all text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-200"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* ── Body ─────────────────────────────────────────────────── */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20 gap-3">
              <div className="relative w-10 h-10">
                <div className="absolute inset-0 rounded-full border-2 border-gray-200 dark:border-white/[0.06]" />
                <div className="absolute inset-0 rounded-full border-2 border-transparent border-t-[#FFE100] animate-spin" />
              </div>
              <span className="text-xs text-gray-400 dark:text-gray-500">Loading run history...</span>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-20 gap-3 text-center px-5">
              <div className="p-2.5 rounded-xl bg-red-500/10 text-red-500 ring-1 ring-red-500/20">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-900 dark:text-white mb-1">Failed to load runs</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-4 max-w-sm">{error}</p>
              </div>
              <button
                onClick={loadRunHistory}
                className="inline-flex items-center gap-1.5 px-3.5 py-1.5 bg-gradient-to-r from-[#FFE100] to-[#FFD000] text-gray-900 rounded-lg hover:shadow-[0_0_12px_rgba(255,225,0,0.3)] transition-all text-xs font-semibold"
              >
                Retry
              </button>
            </div>
          ) : runs.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 gap-3 text-center">
              <div className="p-2.5 rounded-xl bg-gray-100 dark:bg-white/[0.04] text-gray-400 dark:text-gray-500">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-900 dark:text-white mb-0.5">No runs yet</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">This automation hasn't been executed yet</p>
              </div>
            </div>
          ) : (
            <div className="p-4 space-y-4">
              {Object.entries(groupedRuns).map(([dateLabel, dateRuns], groupIndex) => (
                <div key={dateLabel} className="animate-stagger-in" style={{ animationDelay: `${groupIndex * 50}ms` }}>
                  {/* Date group header */}
                  <div className="flex items-center gap-3 mb-2 px-1">
                    <h4 className="text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider flex-shrink-0">{dateLabel}</h4>
                    <div className="flex-1 h-px bg-gray-200/60 dark:bg-white/[0.04]" />
                  </div>

                  {/* Runs in this group */}
                  <div className="space-y-1.5">
                    {dateRuns.map((run) => {
                      const effectiveStatusKey = (run.status === 'stopped' && run.run_metadata?.stoppedReason === 'insufficient_credits')
                        ? 'out_of_credits' : run.status;
                      const status = STATUS_CONFIG[effectiveStatusKey] || DEFAULT_STATUS;
                      const duration = formatDuration(run);

                      return (
                        <div
                          key={run.id}
                          className="group bg-white dark:bg-white/[0.02] border border-gray-200/60 dark:border-white/[0.06] rounded-xl hover:border-gray-300 dark:hover:border-white/[0.12] transition-all duration-200 hover:shadow-sm"
                        >
                          <div className="px-4 py-3 flex items-center justify-between gap-3">
                            {/* Left: status + info */}
                            <div className="flex items-center gap-3 flex-1 min-w-0">
                              {/* Status icon */}
                              <div className={`flex-shrink-0 p-1.5 rounded-lg ${status.bg} ${status.text} ring-1 ${status.ring}`}>
                                {status.icon}
                              </div>

                              {/* Info */}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <span className="text-xs font-medium text-gray-900 dark:text-white">
                                    {status.label}
                                  </span>
                                  {(run.status === "running" || run.status === "pending") && (
                                    <span className="relative flex h-1.5 w-1.5">
                                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75" />
                                      <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-blue-500" />
                                    </span>
                                  )}
                                </div>
                                <div className="flex items-center gap-1.5 mt-0.5">
                                  <span className="text-[11px] text-gray-400 dark:text-gray-500 font-mono tabular-nums">
                                    {formatDate(run.started_at || run.created_at)}
                                  </span>
                                  {duration && (
                                    <>
                                      <span className="text-gray-300 dark:text-white/[0.08]">·</span>
                                      <span className="text-[11px] text-gray-400 dark:text-gray-500 font-mono tabular-nums">{duration}</span>
                                    </>
                                  )}
                                  <span className="text-gray-300 dark:text-white/[0.08]">·</span>
                                  <span className="text-[11px] text-gray-400 dark:text-gray-500 font-medium">
                                    {getTriggerTypeLabel(run.trigger_type)}
                                  </span>
                                  {run.item_count != null && (
                                    <>
                                      <span className="text-gray-300 dark:text-white/[0.08]">·</span>
                                      <span className="text-[11px] text-gray-400 dark:text-gray-500 tabular-nums">
                                        {run.item_count} {run.item_count === 1 ? "row" : "rows"}
                                      </span>
                                    </>
                                  )}
                                </div>
                                {run.error && (
                                  <p className="text-[11px] text-red-500 dark:text-red-400 font-mono truncate mt-1 max-w-md" title={run.error}>
                                    {run.error}
                                  </p>
                                )}
                              </div>
                            </div>

                            {/* Right: actions */}
                            <div className="flex items-center gap-1.5 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                              <button
                                onClick={() => onViewRunDetails(run.id)}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-white/[0.06] rounded-lg transition-all text-xs font-medium"
                              >
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                </svg>
                                Details
                              </button>
                              {run.table_id && (
                                <button
                                  onClick={() => window.open(`/table/${run.table_id}`, "_blank")}
                                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-[#FFE100] to-[#FFD000] text-gray-900 rounded-lg hover:shadow-[0_0_12px_rgba(255,225,0,0.3)] transition-all text-xs font-semibold"
                                >
                                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                  </svg>
                                  Data
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RunHistoryModal;
